/**
 * Card for a pair.
 */
import * as React from 'react';
import { User, GraduationCap, Calendar, Building2, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import { deriveDisplay, formatPairSubjectsTooltip, type PageActions } from './pair-table-utils';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Props for {@link PairCard}. */
interface PairCardProps {
  pair: SessionStudentTutorPair;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: PageActions;
}

/** In-person pair card (grid view) — same fields as {@link PairRow}. */
export const PairCard: React.FC<PairCardProps> = ({ pair, onView, onDelete, pageActions }) => {
  const canView = pageActions?.canViewRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? false;
  const {
    typeConfig,
    TypeIcon,
    sessionDate,
    sessionTimeRange,
    establishmentName,
    studentName,
    tutorName,
    tutorRoleLabel,
    studentLevel,
    subjects,
    subjectCountLabel,
  } = deriveDisplay(pair);

  return (
    <TableCard onClick={() => onView?.(pair.id)}>
      <div className="data-card-header">
        <div className="flex items-center justify-between gap-2 w-full">
          <LabelBadge
            label={typeConfig.label}
            icon={TypeIcon}
            variant={typeConfig.badgeVariant}
            color={typeConfig.badgeColor}
            size="sm"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <LabelBadge
                    label={subjectCountLabel}
                    icon={BookOpen}
                    variant="ghost"
                    color="var(--color-3-nuance-1)"
                    size="sm"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">{formatPairSubjectsTooltip(subjects)}</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="data-card-content">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
              <GraduationCap className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-xs text-muted-foreground">
                {tutorRoleLabel || getRoleLabel('tutor')}
              </span>
              <span className="font-medium text-foreground text-sm truncate">{tutorName}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
              <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-xs text-muted-foreground">{getRoleLabel('student')}</span>
              <span className="font-medium text-foreground text-sm truncate">{studentName}</span>
              {studentLevel && (
                <span className="text-xs text-muted-foreground truncate">{studentLevel}</span>
              )}
            </div>
          </div>
        </div>

        <div className="pt-3 border-t space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
              <span className="text-xs text-foreground capitalize">{sessionDate}</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" style={{ color: 'var(--color-1-nuance-1)' }} />
              <span className="text-xs text-foreground">{establishmentName}</span>
            </div>
          </div>
          {sessionTimeRange && (
            <span className="text-xs text-muted-foreground">{sessionTimeRange}</span>
          )}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          {canView && (
            <Button
              preset="row-view"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(pair.id);
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(pair.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
